import { useLocation } from "react-router";
import { getQueryParam } from "@/src/services/urlParamService";
import React, { useEffect, useMemo, useRef, useState } from "react";
import LegalbirdLoader from "@/src/components/ContentLoader/LegalbirdLoader";
import Error404Page from "@/src/containers/Pages/Generic/Error404/Error404Page";
import Feedback from "@/src/components/Feedback/Feedback";
import _isError from "lodash/isError";
import _isEmpty from "lodash/isEmpty";
import { useMutation } from "@tanstack/react-query";
import { createResource } from "@/src/services/reactQuery/reactQueryService";
import { CustomerFeedback } from "@/src/types/Entities/CustomerFeedback";
import LayoutProtectedRoutes from "@/src/containers/Layouts/LayoutProtectedRoutes";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import ScrollToTop from "@/src/services/ScrollToTop";

export default function FeedbackPage() {
  const location = useLocation();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const initialPostTriggered = useRef(false);

  const [feedbackEntity, setFeedbackEntity] = useState<CustomerFeedback | null>(null);

  const [hasError, setHasError] = useState(false);
  const searchLocation = location.search;

  const saveFeedback = async (data: Partial<CustomerFeedback>) => {
    createMutation.mutateAsync({ uri: "/feedback", data: data }).then(
      (result) => setFeedbackEntity(result),
      () => setHasError(true),
    );
  };

  useEffect(() => {
    const hash = getQueryParam(searchLocation, "h");
    if (hash && !_isEmpty(hash) && !initialPostTriggered.current) {
      saveFeedback({ hash });
      initialPostTriggered.current = true;
    }
  }, [searchLocation]);

  const feedbackHeadline = useMemo(() => {
    if (!feedbackEntity) {
      return "";
    }
    if (!!feedbackEntity.feedbackDateTime) {
      return "Vielen Dank für Ihre Bewertung!";
    }
    return feedbackEntity.source === "footer" ? "Wie hilfreich war diese E-Mail?" : "Wie gefällt Ihnen Legalbird?";
  }, [feedbackEntity]);

  if (hasError || _isError(feedbackEntity)) {
    return <Error404Page />;
  }

  if (!feedbackEntity) {
    return (
      <LayoutProtectedRoutes>
        <LegalbirdLoader />
      </LayoutProtectedRoutes>
    );
  }

  return (
    <LayoutProtectedRoutes>
      {!!feedbackEntity.feedbackDateTime && <ScrollToTop />}
      <ContentBox headline={feedbackHeadline}>
        <Feedback feedbackEntity={feedbackEntity} saveFeedback={saveFeedback} />
      </ContentBox>
    </LayoutProtectedRoutes>
  );
}
